import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { API_KEYS, WEBHOOKS } from './settingsData';

function StatusBadge({ status }) {
  const cfg = {
    Active: 'bg-green-100 text-green-700',
    Revoked: 'bg-slate-100 text-slate-500',
    Expired: 'bg-red-100 text-red-600',
    Inactive: 'bg-slate-100 text-slate-500',
  };
  return (<span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg?.[status] || cfg?.Revoked}`}>{status}</span>);
}

export default function APIKeysSection({ isAdmin, showToast }) {
  const [keys, setKeys] = useState(API_KEYS);
  const [webhooks, setWebhooks] = useState(WEBHOOKS);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showOneTimeModal, setShowOneTimeModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [newKey, setNewKey] = useState({ name: '', environment: 'Production', expiry: 'No expiry', scopes: [] });
  const [newWebhook, setNewWebhook] = useState({ url: '', events: [], secret: '' });

  const SCOPES = ['Read portfolio', 'Write portfolio', 'Read documents', 'Write documents', 'Read ESG', 'Admin'];
  const EVENTS = ['capital_call.created', 'deal.stage_changed', 'document.uploaded', 'esg.alert_raised', 'milestone.completed'];

  const handleRevoke = (keyId) => {
    setKeys(prev => prev?.map(k => k?.id === keyId ? { ...k, status: 'Revoked' } : k));
    setShowRevokeModal(null);
    showToast('API key revoked');
  };

  const handleGenerate = () => {
    const fake = 'sk_' + (newKey?.environment === 'Production' ? 'live' : 'sand') + '_' + Math.random()?.toString(36)?.slice(2, 18);
    setGeneratedKey(fake);
    setShowGenerateModal(false);
    setShowOneTimeModal(true);
    const newEntry = {
      id: 'key-' + Date.now(),
      name: newKey?.name,
      masked: fake?.slice(0, 8) + '••••••••' + fake?.slice(-4),
      environment: newKey?.environment,
      createdBy: 'Sarah Mitchell',
      createdDate: new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      lastUsed: 'Never',
      expiry: newKey?.expiry,
      status: 'Active',
    };
    setKeys(prev => [newEntry, ...prev]);
    setNewKey({ name: '', environment: 'Production', expiry: 'No expiry', scopes: [] });
  };

  const handleSaveWebhook = () => {
    const entry = {
      id: 'wh-' + Date.now(),
      url: newWebhook?.url?.replace(/(?<=.{20}).*(?=.{10})/, '••••••••'),
      events: newWebhook?.events?.join(', '),
      status: 'Active',
      lastTriggered: 'Never',
    };
    setWebhooks(prev => [...prev, entry]);
    setShowWebhookModal(false);
    setNewWebhook({ url: '', events: [], secret: '' });
    showToast('Webhook saved');
  };

  const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30 focus:border-[#3b4c4c] transition-colors';

  return (
    <div>
      {/* Banner */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 mb-6">
        <Icon name="Info" size={16} color="#d97706" className="mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-800">API keys allow secure programmatic access to the SeedDrive platform. Keep your keys private. <span className="font-semibold">Sample data — for demonstration purposes only.</span></p>
      </div>
      {/* API Keys Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Active API Keys</h3>
            <p className="text-sm text-slate-500">{keys?.length} key{keys?.length !== 1 ? 's' : ''} configured</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] transition-colors shadow-sm"
            >
              <Icon name="Plus" size={15} />
              Generate New API Key
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['Name', 'Key', 'Environment', 'Created By', 'Created', 'Last Used', 'Expiry', 'Status', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys?.map(key => (
                <tr key={key?.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap">{key?.name}</td>
                  <td className="px-4 py-3">
                    <code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">{key?.masked}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      key?.environment === 'Production' ? 'bg-[#3b4c4c]/10 text-[#3b4c4c]' : 'bg-slate-100 text-slate-600'
                    }`}>{key?.environment}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{key?.createdBy}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{key?.createdDate}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{key?.lastUsed}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{key?.expiry}</td>
                  <td className="px-4 py-3"><StatusBadge status={key?.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { navigator.clipboard?.writeText(key?.masked); showToast('API key copied to clipboard'); }}
                        title="Copy key"
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <Icon name="Copy" size={14} />
                      </button>
                      <button
                        onClick={() => setShowRevealModal(true)}
                        title="Reveal key"
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <Icon name="Eye" size={14} />
                      </button>
                      {isAdmin && key?.status === 'Active' && (
                        <button
                          onClick={() => setShowRevokeModal(key?.id)}
                          title="Revoke key"
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                        >
                          <Icon name="Ban" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Webhooks */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Webhooks</h3>
            <p className="text-sm text-slate-500">Configure endpoints to receive real-time event notifications.</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowWebhookModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition-colors"
            >
              <Icon name="Plus" size={15} />
              Add Webhook
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['URL', 'Events', 'Status', 'Last Triggered', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {webhooks?.map(wh => (
                <tr key={wh?.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3"><code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">{wh?.url}</code></td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs">{wh?.events}</td>
                  <td className="px-4 py-3"><StatusBadge status={wh?.status} /></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{wh?.lastTriggered}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors" title="Edit">
                        <Icon name="Pencil" size={14} />
                      </button>
                      <button
                        onClick={() => { setWebhooks(prev => prev?.filter(w => w?.id !== wh?.id)); showToast('Webhook deleted'); }}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors" title="Delete"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-2">Revoke API Key</h3>
            <p className="text-sm text-slate-500 mb-6">Are you sure you want to revoke this key? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowRevokeModal(null)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleRevoke(showRevokeModal)} className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">Revoke Key</button>
            </div>
          </div>
        </div>
      )}
      {/* Reveal Modal */}
      {showRevealModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Icon name="Eye" size={20} color="#d97706" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">Key Visibility</h3>
            </div>
            <p className="text-sm text-slate-600 mb-6">For security, full key values are only shown once at creation. Contact your administrator to rotate this key.</p>
            <button onClick={() => setShowRevealModal(false)} className="w-full py-2.5 rounded-xl text-sm font-semibold bg-[#3b4c4c] text-white hover:bg-[#162d4a] transition-colors">Understood</button>
          </div>
        </div>
      )}
      {/* Generate Key Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-slate-900">Generate New API Key</h3>
              <button onClick={() => setShowGenerateModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <Icon name="X" size={16} color="#94a3b8" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Key Name <span className="text-red-500">*</span></label>
                <input type="text" value={newKey?.name} onChange={e => setNewKey(p => ({ ...p, name: e?.target?.value }))} placeholder="e.g. Portfolio Integration Key" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Environment</label>
                <select value={newKey?.environment} onChange={e => setNewKey(p => ({ ...p, environment: e?.target?.value }))} className={inputCls}>
                  <option>Production</option>
                  <option>Sandbox</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Expiry</label>
                <select value={newKey?.expiry} onChange={e => setNewKey(p => ({ ...p, expiry: e?.target?.value }))} className={inputCls}>
                  <option>No expiry</option>
                  <option>90 days</option>
                  <option>180 days</option>
                  <option>1 year</option>
                  <option>Custom date</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Permission Scopes</label>
                <div className="grid grid-cols-2 gap-2">
                  {SCOPES?.map(scope => (
                    <label key={scope} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newKey?.scopes?.includes(scope)}
                        onChange={e => setNewKey(p => ({ ...p, scopes: e?.target?.checked ? [...p?.scopes, scope] : p?.scopes?.filter(s => s !== scope) }))}
                        className="w-4 h-4 rounded border-slate-300 text-[#3b4c4c] focus:ring-[#3b4c4c]/30"
                      />
                      <span className="text-sm text-slate-700">{scope}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                onClick={handleGenerate}
                disabled={!newKey?.name}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Generate Key
              </button>
            </div>
          </div>
        </div>
      )}
      {/* One-time reveal modal */}
      {showOneTimeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle" size={20} color="#16a34a" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">API Key Generated</h3>
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-900 rounded-xl mb-3">
              <code className="flex-1 text-xs font-mono text-green-400 break-all">{generatedKey}</code>
              <button
                onClick={() => { navigator.clipboard?.writeText(generatedKey); showToast('API key generated'); }}
                className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex-shrink-0"
              >
                <Icon name="Copy" size={14} />
              </button>
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200 mb-5">
              <Icon name="AlertTriangle" size={14} color="#d97706" className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">This key will only be shown once. Copy it now and store it securely.</p>
            </div>
            <button onClick={() => setShowOneTimeModal(false)} className="w-full py-2.5 rounded-xl text-sm font-semibold bg-[#3b4c4c] text-white hover:bg-[#162d4a] transition-colors">
              I've copied the key
            </button>
          </div>
        </div>
      )}
      {/* Add Webhook Modal */}
      {showWebhookModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-slate-900">Add Webhook</h3>
              <button onClick={() => setShowWebhookModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <Icon name="X" size={16} color="#94a3b8" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Endpoint URL</label>
                <input type="url" value={newWebhook?.url} onChange={e => setNewWebhook(p => ({ ...p, url: e?.target?.value }))} placeholder="https://your-server.com/webhook" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Events</label>
                <div className="space-y-2">
                  {EVENTS?.map(ev => (
                    <label key={ev} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newWebhook?.events?.includes(ev)}
                        onChange={e => setNewWebhook(p => ({ ...p, events: e?.target?.checked ? [...p?.events, ev] : p?.events?.filter(x => x !== ev) }))}
                        className="w-4 h-4 rounded border-slate-300 text-[#3b4c4c]"
                      />
                      <code className="text-xs font-mono text-slate-700">{ev}</code>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Secret Token</label>
                <input type="password" value={newWebhook?.secret} onChange={e => setNewWebhook(p => ({ ...p, secret: e?.target?.value }))} placeholder="Webhook signing secret" className={inputCls} />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowWebhookModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                onClick={handleSaveWebhook}
                disabled={!newWebhook?.url}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Webhook
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
